import * as React from "react";
import { cn } from "@/lib/utils";

type AccentColor = "yellow" | "pink" | "blue" | "green" | "black";

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  /** Accent color for the card's hover shadow */
  accentColor?: AccentColor;
  /** Disable the hover lift effect */
  disableHover?: boolean;
}

const shadowColors: Record<AccentColor, string> = {
  yellow: "hover:shadow-[10px_10px_0_0_#FFDC00]",
  pink: "hover:shadow-[10px_10px_0_0_#FF73A9]",
  blue: "hover:shadow-[10px_10px_0_0_#3F5EF4]",
  green: "hover:shadow-[10px_10px_0_0_#00EB90]",
  black: "hover:shadow-[10px_10px_0_0_#000000]",
};

/**
 * SpotlightCard (Neo-Brutalist Version)
 *
 * A card component with hard shadow and lift-on-hover effect.
 * Uses solid colors, 3px black borders, and hard offset shadows.
 */
export function SpotlightCard({
  children,
  className,
  accentColor = "black",
  disableHover = false,
  ...props
}: SpotlightCardProps) {
  return (
    <div
      className={cn(
        // Base card styling
        "rounded-2xl",
        "bg-white",
        "border-3 border-black",
        "shadow-[6px_6px_0_0_#000000]",
        // Hover effect (lift and larger shadow)
        !disableHover && [
          "transition-all duration-200 ease-out",
          "hover:-translate-x-1 hover:-translate-y-1",
          shadowColors[accentColor],
        ],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * SpotlightCardContent
 *
 * Standard content wrapper for SpotlightCard
 */
export function SpotlightCardContent({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("p-6", className)} {...props}>
      {children}
    </div>
  );
}
